import { ENV_VARS } from '@/env';
import { CountrySelect } from '@/views/screens/common';
import { Col, Form, Row } from 'antd';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addToCurrentQuoteCarriers } from '../../../../../redux/common/CommonActions';
import {
  addSpotQuoteBiddersProgress,
  searchSpotQuoteBiddersProgress,
} from '../../../../../redux/shipment/ShipmentActions';
import Button from '../../../../components/Buttons/Button';
import FormCheckBox from '../../../../components/Inputs/FormCheckBox';
import TransportationProviderSelector from '../../../../components/Selectors/AsynchSearch/TransportationProviderSelector';
import TransportationGroupSelector from '../../../../components/Selectors/TransportationGroupSelector';
import BiddingAddModalCard from './BiddingAddModalCard';

const BiddingModal = ({ quoteKey, onCloseModal, spotQuoteBidders, createQuote, status }) => {
  const [searchableCarriers, setSearchableCarriers] = useState([]);
  const dispatch = useDispatch();

  const [searchForm] = Form.useForm();
  const [addProvidersForm] = Form.useForm();
  const formSearchResultProviders = Form.useWatch('carriersList', addProvidersForm);
  const carrierIdValue = Form.useWatch('carrierId', searchForm);
  const transportationGroupIdValue = Form.useWatch('transportationGroupId', searchForm);

  const {
    data: { routingGuidesCarriers },
  } = useSelector((state) => state.shipment.searchableSpotQuoteBidders);
  const isCheckedAllProviders = formSearchResultProviders?.every((i) => i.isChecked);

  useEffect(() => {
    const addedCarrierObjIds = spotQuoteBidders?.reduce((acc, current) => {
      const idValue = current.carrierKey || current.carrierId;

      // biome-ignore lint/performance/noAccumulatingSpread: <explanation>
      return { ...acc, [idValue]: true };
    }, {});

    const filteredRoutingGuideCarriers = routingGuidesCarriers?.filter((carrier) => {
      const idValue = carrier.carrierKey || carrier.carrierId;

      return !addedCarrierObjIds?.[idValue];
    });

    setSearchableCarriers(filteredRoutingGuideCarriers);
    addProvidersForm.setFieldsValue({
      carriersList: filteredRoutingGuideCarriers,
    });
  }, [addProvidersForm, routingGuidesCarriers, spotQuoteBidders]);

  const handleCheckAllProviders = () => {
    const newValues = formSearchResultProviders.map((i) => ({
      ...i,
      isChecked: !isCheckedAllProviders,
    }));
    addProvidersForm.setFieldValue('carriersList', newValues);
  };

  const handleSearchCarrier = async () => {
    try {
      await searchForm.validateFields();
      dispatch(searchSpotQuoteBiddersProgress(searchForm.getFieldsValue(true)));
    } catch (_e) {
      // empty
    }
  };

  const handleAddProvider = () => {
    const carriers = formSearchResultProviders?.reduce(
      (acc, { carrierId, transportationProvider, isChecked, routingGuideId, email }) => {
        return isChecked
          ? [
              // biome-ignore lint/performance/noAccumulatingSpread: <explanation>
              ...acc,
              { carrierId, transportationProvider, routingGuideId, email },
            ]
          : acc;
      },
      [],
    );
    if (createQuote) {
      const checkedCarriers = formSearchResultProviders?.filter((c) => c.isChecked);
      dispatch(addToCurrentQuoteCarriers(checkedCarriers));
    } else {
      carriers.forEach(({ carrierId, transportationProvider, routingGuideId, email }) => {
        const payload = {
          ...quoteKey,
          carrierKey: carrierId,
          carrierName: transportationProvider,
          routingGuideId,
          Emails: email || '',
        };
        dispatch(addSpotQuoteBiddersProgress({ biddingPayload: payload, status }));
      });
    }

    onCloseModal();
  };

  const handleResetSearchFields = () => {
    searchForm.resetFields();
  };

  const providerAndGroupRule = {
    required: !carrierIdValue && !transportationGroupIdValue,
    message: 'Need to enter the Transportation Provider or Transportation group field',
  };

  const isHBUser = ENV_VARS.REACT_APP_THEME === 'halliburton';

  return (
    <div className="h-100 custom-scroll-bar">
      <div className="mx-30 my-16">1. Search for providers by filling out the required parameters</div>
      <Form form={searchForm} name="searchAddProvidersToBidding" className="mx-30">
        <Row gutter={30}>
          <Col sm={8}>
            <TransportationProviderSelector name="carrierId" form={searchForm} rules={[providerAndGroupRule]} />
          </Col>
          <Col sm={8}>
            <CountrySelect name="countryCode" label="Country" />
          </Col>
          <Col sm={8}>
            <TransportationGroupSelector name="transportationGroupId" rules={[providerAndGroupRule]} />
          </Col>
        </Row>
        <Row justify="end" align="middle">
          <Col className="py-5">
            <Row gutter={15}>
              <Col>
                <Button
                  onClick={handleSearchCarrier}
                  dataTestId="bidding-modal-search-button"
                  className={isHBUser ? 'hb-button' : ''}
                >
                  Search
                </Button>
              </Col>

              <Col>
                <Button
                  type="default"
                  onClick={handleResetSearchFields}
                  dataTestId="bidding-modal-clear-all-button"
                  className={isHBUser ? 'hb-button' : ''}
                >
                  Clear all
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      </Form>
      <div className="mx-30 my-16">2. Select providers to add them to bidding</div>
      {searchableCarriers?.length ? (
        <Form name="addProviders" form={addProvidersForm} initialValues={{ carriersList: searchableCarriers }}>
          <div className="mx-30 py-5 custom-scroll-bar">
            <div className="custom-scroll-bar" style={{ maxHeight: 'calc(100vh - 330px)' }}>
              <div className="p-15">
                <FormCheckBox
                  label={<span className="pl-5">All providers ({searchableCarriers.length})</span>}
                  className="m-0"
                  checkBoxClass="normal-checkbox"
                  onChange={handleCheckAllProviders}
                  checked={isCheckedAllProviders}
                />
              </div>
              <div className="custom-scroll-bar mb-30" data-testid="bidding-modal-carriers-list">
                <Form.List name="carriersList">
                  {(carriersList) => (
                    <div>
                      {carriersList.map(({ name }, index) => (
                        <BiddingAddModalCard
                          key={name}
                          name={name}
                          index={index}
                          providersDetails={formSearchResultProviders?.[name]}
                        />
                      ))}
                    </div>
                  )}
                </Form.List>
              </div>
            </div>
            <Row justify="end" align="middle">
              <Col className="px-15 py-5">
                <Row gutter={15}>
                  <Col>
                    <Button
                      dataTestId="bidding-modal-add-providers-button"
                      onClick={handleAddProvider}
                      className={isHBUser ? 'hb-button' : ''}
                    >
                      Add providers
                    </Button>
                  </Col>

                  <Col>
                    <Button
                      type="default"
                      variant="danger"
                      onClick={onCloseModal}
                      dataTestId="bidding-modal-cancel-button"
                      className={isHBUser ? 'hb-button' : ''}
                    >
                      Cancel
                    </Button>
                  </Col>
                </Row>
              </Col>
            </Row>
          </div>
        </Form>
      ) : (
        <Row className="mx-30 custom-scroll-bar h-16 text-center background-grey">
          <p>No search results found</p>
        </Row>
      )}
    </div>
  );
};

export default BiddingModal;
